import {
  createContext,
  useMemo,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { configureFetch } from '@/shared/api/monosanFetch'
import { FullPageLoader } from '@/features/auth/components/FullPageLoader'
import { tokenStore } from './tokenStore'
import { clearLastActivityAt } from './idleActivityStore'
import { login, requestMfaChallenge, submitMfa, type LoginResult } from '@/features/auth/api/login'
import { refresh } from '@/features/auth/api/refresh'
import { logout } from '@/features/auth/api/logout'

type AuthContextValue = {
  isAuthenticated: boolean
  isInitializing: boolean
  login: (username: string, password: string) => Promise<LoginResult>
  requestMfaChallenge: (
    mfaToken: string
  ) => Promise<{ obfuscatedPhoneNumber: string }>
  submitMfa: (mfaToken: string, otp: string) => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

const resetSession = (): void => {
  tokenStore.clear()
  clearLastActivityAt()
}

configureFetch({
  onUnauthorized: refresh,
  onClearAuth: resetSession,
})

const subscribe = (cb: () => void): (() => void) => tokenStore.subscribe(cb)
const getSnapshot = (): boolean => tokenStore.get() !== null

type AuthProviderProps = {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps): ReactNode => {
  const isAuthenticated = useSyncExternalStore(subscribe, getSnapshot)
  const [isInitializing, setIsInitializing] = useState(true)
  const cancelledRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false
    refresh()
      .catch(resetSession)
      .finally(() => {
        if (!cancelledRef.current) setIsInitializing(false)
      })
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isInitializing,
      login,
      requestMfaChallenge,
      submitMfa,
      logout,
    }),
    [isAuthenticated, isInitializing]
  )

  if (isInitializing) return <FullPageLoader />
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
